import { _take, takeOnce } from '../../listenerMiddleware';
import * as appActions from '../app/actions';
import * as categoryActions from '../category/actions';
import * as finderActions from '../finder/actions';
import * as projectActions from '../project/actions';
import * as viewModeActions from '../viewMode/actions';
import { getPermissions } from '../app/selectors';
import { getCategoriesById } from '../category/selectors';
import { Permission } from '../../../general/model';
import { ViewMode } from '../viewMode/model';
import { ALL_CATEGORIES_ID } from '../category/model';
import {
  parseLocationHighlightProjectId,
  parseLocationHighlightCategoryId,
  parseLocationHighlightAssetId,
} from '../lastState/model';

// Deep link from Creative Hub ("Open in Asset Lab"): the URL carries
// ?highlight_project_id=<id>, optionally ?highlight_category_id=<id>, and (when a
// file was selected) ?highlight_asset_id=<id>. Those ids equal the Creative Hub
// project-folder / category-folder / file node ids (= Asset Lab Project.id /
// Category.id / Asset.id). We apply project & category as filters in Assets mode;
// for a file we also select the asset and open its details sidebar. The finder
// `selected` state only changes via `assetSelected` / `lastStateActions.applied`
// (not list loads), so we can set it synchronously without racing the asset load.
export const deepLinkListeners = () => {
  watchInitialized();
};

function watchInitialized() {
  takeOnce(appActions.initialized, async (action, listenerApi) => {
    const { getState, dispatch, signal } = listenerApi;

    const permissions = getPermissions(getState());
    if (!permissions[Permission['fs.assetlab_access']]) return;

    const projectId = parseLocationHighlightProjectId();
    if (projectId === null) return;
    const categoryId = parseLocationHighlightCategoryId();
    const assetId = parseLocationHighlightAssetId();

    // The catalog lives only in Assets mode.
    dispatch(viewModeActions.changed({ viewMode: ViewMode.assets }));

    // Selecting the project triggers a categories reload (app listener), which
    // resets the category selection — so wait for `categoriesChanged` before
    // touching the category (same ordering as the magicStudio reveal flow).
    dispatch(projectActions.projectIdsSelected({ ids: [projectId], saveLastState: false }));

    if (categoryId) {
      await _take(listenerApi, categoryActions.categoriesChanged);
      if (signal.aborted) return;

      // Look up the loaded category to expand the tree down to it. If the folder
      // isn't a real Asset Lab category, the category listener falls back to "all".
      const category = getCategoriesById(getState())[categoryId] ?? null;
      dispatch(
        categoryActions.setExpandedKeys({
          keys: [ALL_CATEGORIES_ID, ...(category?.path ?? []), categoryId],
          saveLastState: false,
        }),
      );
      dispatch(
        categoryActions.categorySelected({
          id: categoryId,
          saveLastState: false,
          log: { category },
        }),
      );
    }

    if (assetId !== null) {
      // A file deep link selects its asset and opens the details sidebar. The
      // details load (detailsRequested) fires off `assetSelected` and resolves the
      // asset by id, independently of the asset grid.
      dispatch(finderActions.assetSelected({ ids: [assetId], firstId: assetId, lastId: assetId }));
      dispatch(finderActions.setDetailsVisible({ visible: true }));
    } else {
      // Folder/category deep link: drop any selection restored from the last
      // session and close the details sidebar for a clean, deterministic state.
      dispatch(finderActions.assetSelected({ ids: [], firstId: undefined, lastId: undefined }));
      dispatch(finderActions.setDetailsVisible({ visible: false }));
    }
  });
}
